import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Add as AddIcon,
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import supplierService from '../../../services/supplierService';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import TableSearch from '../../common/TableSearch';
import ActiveFilter from '../../common/ActiveFilter';
import { buildToggleAction, useCanUpdate } from '../../common/activeToggle';
import { useDataTable } from '../../../hooks/useDataTable';

const SuppliersList = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const canToggle = useCanUpdate('suppliers.update');
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Page, size, search, sort and the active filter live in the URL (/suppliers?page=0&size=20&...).
    const table = useDataTable(supplierService.getSuppliersPage, {}, {
        onError: () => toast.error(t('suppliers.fetchError')),
    });

    const handleToggleActive = async (supplier) => {
        try {
            await supplierService.toggleActive(supplier.id);
            toast.success(supplier.isActive === false ? t('common.activated') : t('common.deactivated'));
            table.refresh();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.toggleError'));
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            await supplierService.deleteSupplier(selectedSupplier.id);
            toast.success(t('suppliers.deleteSuccess'));
            setDeleteDialogOpen(false);
            setSelectedSupplier(null);
            table.refresh();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.deleteError'));
        }
    };

    const columns = [
        { field: 'name', headerName: t('common.name'), flex: 1, minWidth: 160 },
        {
            field: 'storeNames',
            headerName: t('suppliers.stores'),
            width: 220,
            sortable: false,
            cellRenderer: (params) =>
                (params.value || []).length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', height: '100%' }}>
                        {params.value.map((storeName) => (
                            <Chip key={storeName} label={storeName} size="small" variant="outlined" />
                        ))}
                    </Box>
                ) : '-',
            tooltipValueGetter: (params) => (params.value || []).join(', ') || null,
        },
        { field: 'contactName', headerName: t('suppliers.contactName'), width: 160, valueFormatter: (p) => p.value || '-' },
        { field: 'email', headerName: t('suppliers.email'), width: 220, valueFormatter: (p) => p.value || '-' },
        { field: 'phone', headerName: t('suppliers.phone'), width: 150, valueFormatter: (p) => p.value || '-' },
        {
            field: 'city',
            headerName: t('common.address'),
            width: 200,
            valueGetter: (p) => {
                const { city, country } = p.data || {};
                return city && country ? `${city}, ${country}` : city || country || '-';
            },
        },
        {
            field: 'isActive',
            headerName: t('common.status'),
            width: 130,
            cellRenderer: (params) => (
                <Chip
                    icon={params.value !== false ? <ActiveIcon /> : <InactiveIcon />}
                    label={params.value !== false ? t('common.active') : t('common.inactive')}
                    color={params.value !== false ? 'success' : 'default'}
                    size="small"
                />
            ),
        },
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 150,
            sortable: false,
            cellRenderer: (params) => (
                <GridActionsCell
                    actions={[
                        {
                            key: 'view',
                            icon: ViewIcon,
                            title: t('common.details'),
                            color: 'info',
                            onClick: () => navigate(`/suppliers/${params.data.id}`),
                        },
                        {
                            key: 'edit',
                            icon: EditIcon,
                            title: t('common.edit'),
                            color: 'primary',
                            onClick: () => navigate(`/suppliers/edit/${params.data.id}`),
                        },
                        buildToggleAction({ row: params.data, onToggle: handleToggleActive, t, canToggle }),
                        {
                            key: 'delete',
                            icon: DeleteIcon,
                            title: t('common.delete'),
                            color: 'error',
                            onClick: () => {
                                setSelectedSupplier(params.data);
                                setDeleteDialogOpen(true);
                            },
                        },
                    ]}
                />
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">{t('suppliers.title')}</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/suppliers/create')}
                >
                    {t('suppliers.createSupplier')}
                </Button>
            </Box>

            <Card>
                <CardContent>
                    <TableSearch
                        placeholder={t('suppliers.searchPlaceholder')}
                        value={table.searchInput}
                        onChange={table.handleSearchChange}
                        sx={{ mb: 2 }}
                    />

                    <ActiveFilter
                        value={table.params.active || 'all'}
                        onChange={(active) => table.updateParams({ active })}
                        sx={{ mb: 2 }}
                    />

                    <AppDataGrid
                        rows={table.data.content}
                        columns={columns}
                        loading={table.loading}
                        paginationMode="server"
                        rowCount={table.data.totalElements}
                        paginationModel={table.paginationModel}
                        onPaginationModelChange={table.handlePaginationModelChange}
                        sortModel={table.sortModel}
                        onSortModelChange={table.handleSortChange}
                        pageSizeOptions={[10, 20, 50, 100]}
                        getRowId={(row) => row.id}
                    />
                </CardContent>
            </Card>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>{t('suppliers.deleteSupplier')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('common.deleteConfirmText', { name: selectedSupplier?.name })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SuppliersList;
